import BillingAccountAddress, {
  CONTACT_TYPE_INVOICING_ADDRESS,
  CONTACT_MEDIUM_TYPE_POSTAL_ADDRESS,
} from './BillingAccountAddress.js';
import BillingAccountPeriod from './BillingAccountPeriod.js';

export const BILLING_ACCOUNT_TYPE = 'BillingAccount';
export const PAYMENT_METHOD_CASH = 'CASH'; // cash payment in person on POS
export const PAYMENT_METHOD_BANK_TRANSFER = 'TRANS'; // manual bank transfer payment
export const PAYMENT_METHOD_CARD = 'CRED'; // manual credit/debit card payment
export const PAYMENT_METHOD_CARD_DIRECT_DEBIT = 'CRDD'; // automated credit/debit card payments
export const PAYMENT_METHOD_DIRECT_DEBIT = 'DD'; // a.k.a "inkaso"
export const PAYMENT_METHOD_DIRECT_DEBIT_TATRA = 'DDTB'; // Tatra Banka specific
export const PAYMENT_METHOD_DIRECT_DEBIT_UNICREDIT = 'DDUCR'; // UniCredit Banka specific
export const PAYMENT_METHOD_DIRECT_DEBIT_SLSP = 'DDSLS'; // Slovenska Sporitelna specific
export const PAYMENT_METHOD_NONE = 'NONE';
export const PAYMENT_METHODS = [
  PAYMENT_METHOD_CASH,
  PAYMENT_METHOD_BANK_TRANSFER,
  PAYMENT_METHOD_CARD,
  PAYMENT_METHOD_CARD_DIRECT_DEBIT,
  PAYMENT_METHOD_DIRECT_DEBIT,
  PAYMENT_METHOD_DIRECT_DEBIT_TATRA,
  PAYMENT_METHOD_DIRECT_DEBIT_UNICREDIT,
  PAYMENT_METHOD_DIRECT_DEBIT_SLSP,
  PAYMENT_METHOD_NONE,
];
export const PAYMENT_METHODS_ONE_OFF = [
  PAYMENT_METHOD_CASH,
  PAYMENT_METHOD_BANK_TRANSFER,
  PAYMENT_METHOD_CARD,
  PAYMENT_METHOD_NONE,
];
export const PAYMENT_METHODS_RECURRENT = [
  PAYMENT_METHOD_CARD_DIRECT_DEBIT,
  PAYMENT_METHOD_DIRECT_DEBIT,
  PAYMENT_METHOD_DIRECT_DEBIT_TATRA,
  PAYMENT_METHOD_DIRECT_DEBIT_UNICREDIT,
  PAYMENT_METHOD_DIRECT_DEBIT_SLSP,
];
export const PAYMENT_METHODS_CARD_RECURRENT = [PAYMENT_METHOD_CARD_DIRECT_DEBIT];
export const PAYMENT_METHODS_DEBIT_RECURRENT = [
  PAYMENT_METHOD_DIRECT_DEBIT,
  PAYMENT_METHOD_DIRECT_DEBIT_TATRA,
  PAYMENT_METHOD_DIRECT_DEBIT_UNICREDIT,
  PAYMENT_METHOD_DIRECT_DEBIT_SLSP,
];
export const INVOICE_PRESENTATION_MEDIUM_E_INVOICE = 'EINVOICE';
export const ACCOUNT_BALANCE_TYPE_CURRENT = 'actualBalance';
export const ACCOUNT_BALANCE_TYPE_OVERDUE = 'overdueBalance';
export const ACCOUNT_BALANCE_TYPES = [ACCOUNT_BALANCE_TYPE_CURRENT, ACCOUNT_BALANCE_TYPE_OVERDUE];

/**
 * Parser model representing the response of BillingAccount API.
 */
export default class BillingAccountInfo {
  /**
   * @param {object} data - Raw response data from BillingAccount API.
   */
  constructor(data) {
    this._data = data;
  }

  /**
   * Validate and return raw BillingAccount data.
   *
   * @return {object}
   */
  get data() {
    const data = this._data;
    if (!data || data['@type'] !== BILLING_ACCOUNT_TYPE) {
      throw new Error('Invalid BillingAccount data.');
    }
    return data;
  }

  /**
   * Validate and return the default payment method.
   *
   * @return {?string}
   */
  get defaultPaymentMethod() {
    return this.data?.defaultPaymentMethod?.id;
  }

  /**
   * Check if the default payment method is a card recurring.
   *
   * @return {boolean}
   */
  get hasRecurrentPaymentsByCard() {
    return PAYMENT_METHODS_CARD_RECURRENT.includes(this.defaultPaymentMethod);
  }

  /**
   * Check if the default payment method is one of the debit recurring ones.
   *
   * @return {boolean}
   */
  get hasRecurrentPaymentsByDebit() {
    return PAYMENT_METHODS_DEBIT_RECURRENT.includes(this.defaultPaymentMethod);
  }

  /**
   * Check if the default payment method is one of the one-off ones.
   *
   * @return {boolean}
   */
  get hasOneOffPayments() {
    return PAYMENT_METHODS_ONE_OFF.includes(this.defaultPaymentMethod);
  }

  /**
   * Check if the default payment method is one of the recurring ones.
   *
   * @return {boolean}
   */
  get hasRecurrentPayments() {
    return PAYMENT_METHODS_RECURRENT.includes(this.defaultPaymentMethod);
  }

  /**
   * Validate and return the billing postal address, if present.
   *
   * @return {?BillingAccountAddress}
   */
  get invoicingAddress() {
    const address = this._contactSection.find(
      item => item.contactType === CONTACT_TYPE_INVOICING_ADDRESS,
    );
    if (!address) {
      return null;
    }
    const contactMedia = address.contactMedium;
    if (!Array.isArray(contactMedia)) {
      throw new Error("BillingAccount invoicing address is missing 'contactMedium' section.");
    }
    const contactMedium = contactMedia.find(
      medium => medium.mediumType === CONTACT_MEDIUM_TYPE_POSTAL_ADDRESS && medium.preferred,
    );
    if (!contactMedium) {
      throw new Error(
        "BillingAccount invoicing address is missing preferred 'postalAddress' contact medium.",
      );
    }
    const { characteristic } = contactMedium;
    if (!characteristic) {
      throw new Error(
        'BillingAccount invoicing address contact medium is missing characteristic section.',
      );
    }

    return new BillingAccountAddress({
      ...characteristic,
    });
  }

  /**
   * Validate and return the billing period.
   * Calculates the 'from' and 'to' dates of the billing period based on the 'dateShift' value.
   * For 'dateShift' values 1 to 27 the billing period is 'dateShift+1' day of previous month to 'dateShift' day of
   * current month, but for the value of 28, the billing period is simply from start of current month to the end of it.
   *
   * @return {?BillingAccountPeriod}
   */
  get billingPeriod() {
    const dateShift = this._billingStructure?.cycleSpecification?.dateShift;
    if (!Number.isSafeInteger(dateShift)) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const to = (() => {
      if (dateShift === 28) {
        const lastDayOfMonth = new Date(today);
        lastDayOfMonth.setMonth(lastDayOfMonth.getMonth() + 1);
        lastDayOfMonth.setDate(0);
        return lastDayOfMonth;
      }
      const date = new Date(today);
      date.setDate(dateShift);
      return date;
    })();

    const from = (() => {
      if (dateShift === 28) {
        const firstDayOfMonth = new Date(today);
        firstDayOfMonth.setDate(1);
        return firstDayOfMonth;
      }
      const date = new Date(today);
      date.setMonth(date.getMonth() - 1);
      date.setDate(dateShift + 1);
      return date;
    })();

    // If we're past the `to` date in the current month, we're in the new billing period
    if (today > to) {
      to.setMonth(to.getMonth() + 1);
      from.setMonth(from.getMonth() + 1);
    }

    return new BillingAccountPeriod({ from, to });
  }

  /**
   * Determine whether the user has e-invoice active.
   *
   * @return {boolean}
   */
  get hasEInvoice() {
    // If the user doesn't have the billing structure section or the presentation media section,
    // such as prepaid users, we assume they don't have e-invoice
    if (!this._billingStructure) {
      return false;
    }

    const { presentationMedia } = this._billingStructure;
    if (!Array.isArray(presentationMedia) || presentationMedia.length !== 1) {
      return false;
    }

    const presentationMedium = presentationMedia[0];

    return presentationMedium?.id === INVOICE_PRESENTATION_MEDIUM_E_INVOICE;
  }

  /**
   * Validate and return the amount the user is currently due to pay.
   *
   * @return {?number}
   */
  get duePayment() {
    if (this._accountBalance === null) {
      return null;
    }
    const currentBalance = this._accountBalance.find(
      item => item.balanceType === ACCOUNT_BALANCE_TYPE_CURRENT,
    );
    const value = currentBalance?.amount.value;
    if (!Number.isFinite(value)) {
      throw new Error(
        "BillingAccount data has invalid 'currentBalance' in 'accountBalance' section.",
      );
    }
    return value;
  }

  /**
   * @return {?number}
   */
  get creditRefillLimit() {
    return this._data.prepayBalanceRechargeMonthlyLimit ?? null;
  }

  /**
   * @return {?number}
   */
  get creditRefillLimitRemaining() {
    return this._data.prepayBalanceRechargeRemainingLimit ?? null;
  }

  /**
   * @return {object[]}
   * @private
   */
  get _contactSection() {
    const { contact } = this.data;
    if (!Array.isArray(contact)) {
      throw new Error("BillingAccount data doesn't contain contact information section.");
    }
    return contact;
  }

  /**
   * @return {?object}
   * @private
   */
  get _billingStructure() {
    const { billStructure: billingStructureArray } = this.data;
    if (!Array.isArray(billingStructureArray) || billingStructureArray.length !== 1) {
      return null;
    }
    return billingStructureArray[0];
  }

  /**
   * @return {?(object[])}
   * @private
   */
  get _accountBalance() {
    const { accountBalance } = this.data;
    if (!Array.isArray(accountBalance) || accountBalance.length !== 2) {
      return null;
    }
    return accountBalance;
  }

  /**
   * Construct the request body for a single generic PATCH operation, given the correct path and value.
   *
   * @param {string} path
   * @param {*} value
   * @return {{op: string, path: string, value: *}[]}
   */
  static createPatchBody(path, value) {
    return [
      {
        op: 'replace',
        path,
        value,
      },
    ];
  }
}
